import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Rocket,
  Zap,
  Shield,
  Users,
  DollarSign,
  Headphones,
  Clock3,
  MessageSquareText,
  FileCheck2,
  Layers3,
  Monitor,
  Smartphone,
  Cloud,
  Wrench,
} from 'lucide-react';
import './Home.css';

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
};

const stagger = {
  animate: {
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const features = [
  { icon: Rocket, title: 'Innovation First', desc: 'Cutting-edge technologies and modern development practices to keep you ahead.', color: '#e85d04' },
  { icon: Zap, title: 'Fast Delivery', desc: 'Agile methodology ensuring rapid deployment and quick time-to-market.', color: '#f59e0b' },
  { icon: Shield, title: 'Secure & Reliable', desc: 'Enterprise-grade security and robust infrastructure for peace of mind.', color: '#10b981' },
  { icon: Users, title: 'Expert Team', desc: 'Experienced professionals with deep domain knowledge and proven track record.', color: '#6366f1' },
  { icon: DollarSign, title: 'Cost Effective', desc: 'Competitive pricing without compromising on quality or service.', color: '#8b5cf6' },
  { icon: Headphones, title: '24/7 Support', desc: 'Round-the-clock technical support to keep your systems running smoothly.', color: '#ec4899' },
];

const floatingServices = [
  { icon: Monitor, label: 'Web Development' },
  { icon: Smartphone, label: 'Mobile Apps' },
  { icon: Cloud, label: 'Cloud Solutions' },
  { icon: Wrench, label: 'IT Support' },
];

const stats = [
  { value: '100+', label: 'Projects Completed' },
  { value: '50+', label: 'Happy Clients' },
  { value: '5+', label: 'Years Experience' },
  { value: '99%', label: 'Client Satisfaction' },
];

const usClientBenefits = [
  {
    icon: Clock3,
    title: 'US Time-Zone Overlap',
    desc: 'Daily overlap for standups, sprint reviews, and faster decision cycles.',
  },
  {
    icon: MessageSquareText,
    title: 'Clear Communication',
    desc: 'Structured updates, sprint-based delivery, and transparent weekly reporting.',
  },
  {
    icon: FileCheck2,
    title: 'Security & NDA Ready',
    desc: 'NDA-friendly workflows, secure repositories, and role-based access controls.',
  },
  {
    icon: Layers3,
    title: 'End-to-End Product Teams',
    desc: 'Design, development, QA, and support in one delivery pipeline.',
  },
];

const Home = () => {
  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-blob hero-blob-1" />
          <div className="hero-blob hero-blob-2" />
          <div className="hero-blob hero-blob-3" />
          <div className="hero-grid" />
        </div>
        <div className="hero-content">
          <div className="container">
            <div className="hero-inner">
              <motion.div
                className="hero-text"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              >
                <p className="hero-badge">Software Partner for US Startups & SMBs</p>
                <h1 className="hero-title">
                  Transform your digital vision into{' '}
                  <span className="hero-highlight">reality</span>
                </h1>
                <p className="hero-subtitle">
                  LogicSphere Technologies builds web, mobile, and business software products for fast-growing teams.
                  From MVPs to full-scale systems, we deliver secure and reliable software with clear communication.
                </p>
                <div className="hero-buttons">
                  <Link to="/services" className="btn">
                    Our Services
                  </Link>
                  <Link to="/contact" className="btn btn-secondary">
                    Book a Free Discovery Call
                  </Link>
                </div>
              </motion.div>
              <motion.div
                className="hero-visual"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="hero-cards">
                  {floatingServices.map((item, i) => (
                    <motion.div
                      key={i}
                      className="hero-card"
                      style={{ '--delay': `${i * 0.15}s` }}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }}
                      whileHover={{ scale: 1.05, y: -4 }}
                    >
                      <item.icon size={28} strokeWidth={1.8} />
                      <span>{item.label}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features section">
        <div className="container">
          <motion.h2
            className="section-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5 }}
          >
            Why Choose LogicSphere?
          </motion.h2>
          <motion.p
            className="section-subtitle"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            We combine technical expertise with business acumen to deliver solutions that drive growth.
          </motion.p>
          <motion.div
            className="features-grid"
            variants={stagger}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: '-40px' }}
          >
            {features.map((item, i) => (
              <motion.div
                key={i}
                className="feature-card"
                variants={fadeUp}
                whileHover={{ y: -8, transition: { duration: 0.2 } }}
                data-accent
                style={{ '--accent': item.color }}
              >
                <div className="feature-icon-wrap">
                  <item.icon size={24} strokeWidth={2} />
                </div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats section">
        <div className="container">
          <motion.div
            className="stats-grid"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {stats.map((item, i) => (
              <motion.div
                key={i}
                className="stat-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
              >
                <div className="stat-number">{item.value}</div>
                <div className="stat-label">{item.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* US Clients Section */}
      <section className="us-clients section">
        <div className="container">
          <motion.h2
            className="section-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5 }}
          >
            Built for US businesses
          </motion.h2>
          <motion.p
            className="section-subtitle"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            We help startups and growing companies ship software faster with reliable communication and predictable delivery.
          </motion.p>

          <div className="us-grid">
            {usClientBenefits.map((item, index) => (
              <motion.div
                key={index}
                className="us-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
              >
                <div className="us-card-icon">
                  <item.icon size={22} />
                </div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta section">
        <div className="cta-bg">
          <div className="cta-blob" />
        </div>
        <div className="container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2>Ready to transform your business?</h2>
            <p>Let's discuss how LogicSphere can help you achieve your digital goals.</p>
            <Link to="/contact" className="btn">
              Start Your Project
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
